import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, Chip, Grid, Link, Paper, Stack, Typography } from '@mui/material';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ContextStore } from '../store/contextStore';
import { useDeveloperApi } from './api';
import BreakerBanner from './BreakerBanner';
import { CopyButton, DeveloperShell, EnvironmentChip, ErrorAlert, Field, formatTs, Loading, mono } from './components';
import { DEVELOPER_PAGES, smsState } from './labels';
import MessagingPanel from './MessagingPanel';
import MirrorPanel from './MirrorPanel';
import OutboundEditor from './OutboundEditor';
import QuotaPanel, { LanePausedBanner } from './QuotaPanel';
import { pagePath } from './routes';
import SandboxCard from './SandboxCard';
import SandboxSyncNote from './SandboxSyncNote';
import SuspensionPanel from './SuspensionPanel';
import type { BreakerAction, DeveloperOutboundUpdate, DeveloperOverview } from './types';

const Section = ({ title, children, testId }: { title: string; children: React.ReactNode; testId?: string }) => (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }} data-testid={testId}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>{title}</Typography>
        {children}
    </Paper>
);

const ReportLine = ({ label, report }: { label: string; report: Record<string, any> | null }) => {
    if (!report) {
        return null;
    }
    const when = report.finished_at || report.started_at || report.requested_at;
    return (
        <Typography variant="body2">
            {label}: {report.status || 'recorded'}{when ? `, ${formatTs(when)}` : ''}
            {report.error ? ` (${report.error})` : ''}
        </Typography>
    );
};

/** The Developer Overview (SBX D9): environment, ingress, outbound, suspension, quotas and the circuit
 *  breaker. Production shows what it has; the sandbox-only blocks render only for a sandbox. */
const OverviewPage = () => {
    const api = useDeveloperApi();
    const { setDeveloperEnvironment, hasPerm } = React.useContext(ContextStore) as any;
    const [overview, setOverview] = React.useState<DeveloperOverview | null>(null);
    const [error, setError] = React.useState<unknown>(null);
    const [loading, setLoading] = React.useState(true);

    const load = React.useCallback(() => {
        setLoading(true);
        setError(null);
        return api.overview()
            .then((data) => {
                setOverview(data);
                // Corrects a session stored before the backend sent `environment` (menu, badge, banner).
                setDeveloperEnvironment?.(data);
            })
            .catch(setError)
            .finally(() => setLoading(false));
    }, [api, setDeveloperEnvironment]);

    React.useEffect(() => {
        load();
    }, [load]);

    if (!overview) {
        return (
            <DeveloperShell title="Overview">
                {loading ? <Loading /> : <ErrorAlert error={error} onRetry={load} />}
            </DeveloperShell>
        );
    }

    const sandbox = overview.environment === 'sandbox';
    const perms = overview.permissions;
    // Production: its sandbox's SMS state (DSEC D5), shown on the sandbox card (D3's `extra` slot) when there is one.
    const sandboxSms = !sandbox && overview.sms ? (
        <Field label="Sandbox SMS">
            <span data-testid="sandbox-sms">
                {smsState(overview.sms.state).label}
                {overview.sms.state === 'on' && overview.sms.number ? `, ${overview.sms.number}` : ''}
            </span>
        </Field>
    ) : null;

    const saveOutbound = async (update: DeveloperOutboundUpdate) => {
        const outbound = await api.updateOutbound(update);
        setOverview((prev) => (prev ? { ...prev, outbound } : prev));
        return outbound;
    };
    const setSuspension = async (suspended: boolean, reason?: string) => {
        const state = await api.setSuspension(suspended, reason);
        await load();
        return state;
    };
    const setMirror = async (enabled: boolean) => {
        const mirror = await api.setMirror(enabled);
        await load();
        return mirror;
    };
    // Production's switch for its sandbox's mirror (ESB): the card is read again, as the sandbox's own switch does.
    const setSandboxMirror = async (enabled: boolean) => {
        const mirror = await api.setSandboxMirror(enabled);
        await load();
        return mirror;
    };
    const resetBreaker = async (action: BreakerAction) => {
        const result = await api.resetBreaker(action);
        await load();
        return result;
    };

    return (
        <DeveloperShell title="Overview">
            {error ? <ErrorAlert error={error} onRetry={load} /> : null}
            {sandbox && (
                <BreakerBanner breaker={overview.breaker} paused={overview.paused} canReset={perms.outbound_manage} onReset={resetBreaker} />
            )}
            {sandbox && <LanePausedBanner throttle={overview.quotas?.throttle} />}
            {sandbox && <SandboxSyncNote linked={!!(hasPerm?.('ops', 'sync_bookings') || hasPerm?.('import', 'sync_bookings'))} />}
            {overview.shared_vendor_quota && (
                <Alert severity="info" sx={{ my: 1 }} data-testid="shared-vendor-quota">{overview.shared_vendor_quota.text}</Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} md={6}>
                    <Section title="Environment" testId="environment-section">
                        <Stack spacing={1.5}>
                            <Field label="This dashboard">
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <span>{overview.tenant.name} ({overview.tenant.code})</span>
                                    <EnvironmentChip environment={overview.environment} />
                                </Stack>
                            </Field>
                            {overview.paired && (
                                <Field label={sandbox ? 'Production' : 'Sandbox'}>
                                    <Link href={overview.paired.dashboard_url} target="_blank" rel="noopener noreferrer">
                                        {overview.paired.name} ({overview.paired.code})
                                        <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
                                    </Link>
                                </Field>
                            )}
                            {!overview.sandbox_card && sandboxSms}
                            <Field label="API base URL">
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Typography sx={mono} data-testid="ingress-base-url">{overview.ingress_base_url}</Typography>
                                    <CopyButton value={overview.ingress_base_url} label="API base URL" />
                                </Stack>
                            </Field>
                            {sandbox && overview.ingress && (
                                <Field label="Ingress rule">
                                    {overview.ingress.mode === 'enforce'
                                        ? 'Requests that do not use the address above are refused (421).'
                                        : 'Requests that do not use the address above are accepted and recorded.'}
                                </Field>
                            )}
                            {sandbox && (
                                <Field label="Developer notifications go to">
                                    {overview.developer_contacts.length > 0 ? overview.developer_contacts.join(', ') : 'Nobody yet: ask Curated Planet to add a contact.'}
                                </Field>
                            )}
                        </Stack>
                    </Section>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Section title="Developer pages">
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ rowGap: 1 }}>
                            {overview.pages.filter((p) => p !== 'overview').map((p) => (
                                <Chip key={p} label={DEVELOPER_PAGES[p] || p} component={RouterLink} to={pagePath(p)} clickable variant="outlined" />
                            ))}
                        </Stack>
                        {sandbox && (
                            <Box sx={{ mt: 2 }}>
                                <ReportLine label="Content copied from production" report={overview.clone} />
                                <ReportLine label="Last reset" report={overview.reset} />
                            </Box>
                        )}
                    </Section>
                </Grid>
                {!sandbox && overview.sandbox_card && (
                    <Grid item xs={12}>
                        <SandboxCard
                            card={overview.sandbox_card}
                            canSignIn={!!perms.sandbox_signin}
                            onResumed={load}
                            onAdminAdded={load}
                            canManageMirror={!!perms.sandbox_mirror_manage}
                            onMirrorChange={setSandboxMirror}
                            extra={sandboxSms}
                        />
                    </Grid>
                )}
                {sandbox && (
                    <Grid item xs={12}>
                        <Section title="Outbound messages" testId="outbound-section">
                            <OutboundEditor outbound={overview.outbound} canManage={perms.outbound_manage} onSave={saveOutbound} />
                        </Section>
                    </Grid>
                )}
                {sandbox && overview.quotas && (
                    <Grid item xs={12} md={6}>
                        <Section title="Quotas">
                            <QuotaPanel quotas={overview.quotas} />
                        </Section>
                    </Grid>
                )}
                {sandbox && overview.suspension && (
                    <Grid item xs={12} md={6}>
                        <Section title="Suspend sandbox">
                            <SuspensionPanel suspension={overview.suspension} canManage={perms.outbound_manage} onChange={setSuspension} />
                        </Section>
                    </Grid>
                )}
                {sandbox && (
                    <Grid item xs={12} md={6}>
                        <Section title="Messaging" testId="messaging-section">
                            <MessagingPanel outbound={overview.outbound} canManage={perms.outbound_manage} onOutboundSave={saveOutbound} onChanged={load} />
                        </Section>
                    </Grid>
                )}
                {sandbox && overview.mirror && (
                    <Grid item xs={12} md={6}>
                        <Section title="Mirror of production's webhooks" testId="mirror-section">
                            <MirrorPanel mirror={overview.mirror} canManage={!!perms.mirror_manage} outboundMode={overview.outbound.mode} onChange={setMirror} />
                        </Section>
                    </Grid>
                )}
            </Grid>
            {!sandbox && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    This is your production environment.
                    {overview.paired ? ' Build and test integrations in the sandbox first: its messages stay in an outbox.' : ''}
                </Typography>
            )}
            <Box sx={{ mt: 2 }}>
                <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
            </Box>
        </DeveloperShell>
    );
};

export default OverviewPage;
